import { ChipProps, Chip } from '@mui/material';

/*
 * Palette paths only, so tags follow the theme's mode (dark ramps keep each step's role).
 *
 * A tag's tint and border carry its hue; its label carries the meaning, so the label is the part
 * that has to be readable. Each `color` below is the lightest step of its own ramp that clears
 * WCAG AA (4.5:1) against that tag's own background, measured rather than guessed — the label is
 * 11px uppercase, which is small text and gets no large-text exemption.
 *
 * The `.main` steps these replaced measured between 2.04:1 and 4.42:1. The worst was `warning`, at
 * 2.04:1, which is what "Due in 2 days" and "AutoPayroll off" were set in.
 */
export const tagColors = {
    success: {
        border: 'success.light',
        backgroundColor: 'green.50',
        // green.500 measured 4.17:1 on green.50; 600 is the first passing step, at 4.93:1.
        color: 'green.600',
    },
    warning: {
        border: 'warning.light',
        backgroundColor: 'orange.50',
        // The orange ramp stays light through its 800 (4.06:1), so the label takes 900, at 6.95:1.
        color: 'orange.900',
    },
    error: {
        border: 'error.light',
        backgroundColor: 'red.50',
        // red.500 measured 2.99:1 on red.50; 800 is the first passing step, at 5.89:1.
        color: 'red.800',
    },
    info: {
        border: 'info.light',
        backgroundColor: 'cement.50',
        // cement.500 measured 2.66:1 on cement.50; 700 is the first passing step, at 6.26:1.
        color: 'cement.700',
    },
    primary: {
        border: 'primary.light',
        backgroundColor: 'purple.50',
        color: 'primary.main',
    },
    secondary: {
        border: 'secondary.light',
        backgroundColor: 'grey.50',
        color: 'secondary.main',
    },
    lightSecondary: {
        border: 'lightSecondary.dark',
        backgroundColor: 'lightSecondary.main',
        // text.secondary measured 3.98:1 on this tint; grey.600 reaches 5.67:1.
        color: 'grey.600',
    },
    cement: {
        border: 'cement.500',
        backgroundColor: 'cement.100',
        /* `cement.contrastText` is not a token — the palette's cement block defines only the ramp,
           `main` and `hover` — so this resolved to nothing and the label fell through to whatever
           colour it inherited. cement.700 is the first passing step, at 5.94:1. */
        color: 'cement.700',
    },
    // Solid green pill for promo callouts; pair with variant="filled".
    successFilled: {
        border: 'success.dark',
        // White on success.main measured 4.46:1, just under AA; success.dark carries it to 6.35:1.
        backgroundColor: 'success.dark',
        color: 'success.contrastText',
    },
    // Solid white pill for use on colored/gradient surfaces (e.g. QTE hero "DUE" pill); page surface in dark.
    white: {
        border: 'background.paper',
        backgroundColor: 'background.paper',
        color: 'text.primary',
    },
    /*
     * Tinted: a fill one or two steps deeper than the keys above, for a status pill that has to
     * read as a shape on a white page. The `.50` fills above measure 1.04–1.11:1 against white, so
     * only their label marks them out; these reach 1.22–1.37:1 while every label still clears AA.
     * Pair with variant="filled".
     */
    successTinted: {
        border: 'green.100',
        backgroundColor: 'green.100',
        // 5.22:1 on green.100.
        color: 'green.700',
    },
    warningTinted: {
        border: 'orange.200',
        backgroundColor: 'orange.200',
        // 5.26:1 on orange.200.
        color: 'orange.900',
    },
    errorTinted: {
        border: 'red.200',
        backgroundColor: 'red.200',
        // red.100 reached only 1.19:1 against white; red.200 is 1.66:1, and red.900 on it 5.64:1.
        color: 'red.900',
    },
    primaryTinted: {
        border: 'purple.100',
        backgroundColor: 'purple.100',
        // 8.97:1 on purple.100.
        color: 'primary.main',
    },
    brandPurpleTinted: {
        border: 'brandPurple.tint',
        backgroundColor: 'brandPurple.tint',
        // 6.20:1 on the tint.
        color: 'brandPurple.main',
    },
    warningSoft: {
        border: 'orange.100',
        backgroundColor: 'orange.100',
        // 6.46:1 on orange.100.
        color: 'orange.900',
    },
    cementTinted: {
        border: 'cement.300',
        backgroundColor: 'cement.300',
        // 5.01:1 on cement.300.
        color: 'cement.700',
    },
    // Borderless cement pill with a plain black label, for a tag that states a fact rather than a
    // status — the surrounding surface carries the emphasis, not the pill. Pair with variant="filled".
    cementFilled: {
        border: 'cement.200',
        backgroundColor: 'cement.200',
        color: 'text.primary',
    },
};

interface TagOwnProps {
    color: keyof typeof tagColors;
    /** Keep the label as written instead of the uppercase overline default (e.g. "$0 promo"). */
    preserveLabelCase?: boolean;
    /**
     * Tints the icon apart from the label. Defaults to the label's color, which is what every tag
     * whose icon and text carry the same weight wants — pass this only when the icon is doing the
     * signalling on its own, e.g. a red calendar beside a neutral date.
     */
    iconColor?: string;
}

export default function Tag({
    color,
    size,
    sx,
    preserveLabelCase = false,
    iconColor,
    ...props
}: TagOwnProps & Omit<ChipProps, 'color'>) {
    const textColor = tagColors[color].color || tagColors.primary.color;
    const borderColor = tagColors[color].border || tagColors.primary.border;
    const backgroundColor = tagColors[color].backgroundColor || tagColors.primary.backgroundColor;

    return (
        <Chip
            variant="outlined"
            size="small"
            aria-label={`${props.label} tag`}
            sx={{
                ...sx,
                borderRadius: 1,
                color: textColor,
                gap: 1,
                '&:hover': {
                    borderColor,
                    ...(props.variant === 'filled' && { backgroundColor }),
                },
                ...(size !== 'small' && { paddingY: '4px' }),
                ...(size === 'small' && { paddingY: '2px' }),
                '& .MuiChip-icon': {
                    // Zero both: the Chip root already sets `gap: 1`, so MUI's default icon margins
                    // stacked on top of it and pushed the icon off-centre.
                    marginRight: 0,
                    marginLeft: 0,
                    color: iconColor ?? textColor,
                },
                '& .MuiChip-label': {
                    typography: 'overline',
                    ...(preserveLabelCase && { textTransform: 'none' }),
                },
                // Only when an icon precedes it. The label's own left padding is what insets the
                // text from the border on a label-only tag, but after an icon it stacks on the
                // root `gap` and leaves the icon marooned. Sibling selector so both cases work.
                '& .MuiChip-icon + .MuiChip-label': {
                    paddingLeft: 0,
                },
                borderColor,
                ...(props.variant === 'filled' && { backgroundColor }),
            }}
            {...props}
        />
    );
}
